<div class="header">
  <div>
    <h2>{{ "pages.dozent.messages.title" | translate }}</h2>
    <p class="subtitle">{{ "pages.dozent.messages.subtitle" | translate }}</p>
  </div>
  <app-dozent-filter-dropdown (changed)="onDozentFilterChange()"></app-dozent-filter-dropdown>
</div>

<app-page-loader *ngIf="isLoadingLearners"></app-page-loader>

<div class="grid" *ngIf="!isLoadingLearners">
  <!-- ── Left: learner list ─────────────────────────────────────────── -->
  <nb-card class="learners-card" alAnchor="dozent.learner-list">
    <nb-card-header>
      <h6>
        <nb-icon icon="people-outline"></nb-icon>
        {{ "pages.dozent.messages.learnersTitle" | translate }}
        <span class="count-pill">{{ filteredLearners.length }}</span>
      </h6>
    </nb-card-header>

    <div class="panel-toolbar">
      <nb-form-field class="toolbar-search">
        <input
          type="text"
          nbInput
          fieldSize="small"
          fullWidth
          [placeholder]="'pages.dozent.messages.searchPlaceholder' | translate"
          [(ngModel)]="searchText"
          (ngModelChange)="onLearnerSearchChange()"
        />
        <button nbSuffix nbButton ghost size="small">
          @if (searchText && searchText.length > 0) {
            <nb-icon icon="close-outline" (click)="searchText = ''; onLearnerSearchChange()"></nb-icon>
          } @else {
            <nb-icon icon="search-outline"></nb-icon>
          }
        </button>
      </nb-form-field>

      <nb-select
        size="small"
        [selected]="learnerSortMode"
        (selectedChange)="onLearnerSortChange($event)"
        [placeholder]="'pages.dozent.messages.sortBy' | translate"
      >
        <nb-option value="priority">{{ "pages.dozent.messages.sortPriority" | translate }}</nb-option>
        <nb-option value="name_asc">{{ "pages.dozent.messages.sortNameAsc" | translate }}</nb-option>
        <nb-option value="name_desc">{{ "pages.dozent.messages.sortNameDesc" | translate }}</nb-option>
        <nb-option value="last_active_desc">{{ "pages.dozent.messages.sortLastActiveDesc" | translate }}</nb-option>
        <nb-option value="last_active_asc">{{ "pages.dozent.messages.sortLastActiveAsc" | translate }}</nb-option>
      </nb-select>
    </div>

    <div class="learner-scroll" *ngIf="filteredLearners.length > 0">
      <button
        type="button"
        class="learner-row"
        alAnchor="dozent.learner-row"
        *ngFor="let learner of pagedLearners"
        [ngClass]="{
          'is-selected': selectedLearner?.user_id === learner.user_id,
          'has-notifications': hasAnyNotificationMarker(learner)
        }"
        (click)="onSelectLearner(learner)"
      >
        <span
          class="avatar"
          [class.online]="learner.is_online"
          [nbTooltip]="
            (learner.is_online
              ? 'pages.dozent.messages.online'
              : 'pages.dozent.messages.offline'
            ) | translate
          "
          nbTooltipPlacement="top"
          >{{ getInitials(learner) }}</span
        >

        <span class="learner-labels">
          <span class="name">{{ getLearnerName(learner) }}</span>
          <span class="mail">{{ learner.email }}</span>
          <span class="last-active" *ngIf="learner.last_conversation_at">
            <nb-icon icon="clock-outline"></nb-icon>
            {{ learner.last_conversation_at | date: "dd.MM.yy HH:mm" }}
          </span>
        </span>

        <span class="markers" *ngIf="hasAnyNotificationMarker(learner)">
          <span
            *ngIf="hasMessageMarker(learner)"
            class="marker"
            [nbTooltip]="'pages.dozent.messages.markerMessage' | translate"
            nbTooltipPlacement="top"
          >
            <nb-icon icon="email-outline"></nb-icon>
          </span>
          <span
            *ngIf="hasExerciseMarker(learner)"
            class="marker"
            [nbTooltip]="'pages.dozent.messages.markerExercise' | translate"
            nbTooltipPlacement="top"
          >
            <nb-icon icon="book-open-outline"></nb-icon>
          </span>
        </span>
      </button>
    </div>

    <div class="empty-block" *ngIf="filteredLearners.length === 0">
      <span class="empty-block__icon"><nb-icon icon="people-outline"></nb-icon></span>
      <p>{{ "pages.dozent.messages.noLearners" | translate }}</p>
    </div>

    <nb-card-footer *ngIf="filteredLearners.length > 0">
      <app-pagination
        class="toolbar-pagination"
        [totalItems]="filteredLearners.length"
        [itemsPerPage]="learnerPageSize"
        [currentPage]="learnerPage"
        [pageSizeOptions]="pageSizeOptions"
        (pageChanged)="onLearnerPageChange($event)"
        (itemsPerPageChange)="onLearnerPageSizeChange($event)"
      ></app-pagination>
    </nb-card-footer>
  </nb-card>

  <!-- ── Right: thread panel ────────────────────────────────────────── -->
  <nb-card class="thread-card" alAnchor="dozent.thread">
    <!-- No learner selected -->
    <nb-card-body *ngIf="!selectedLearner" class="empty-panel">
      <span class="empty-block__icon empty-block__icon--lg">
        <nb-icon icon="message-square-outline"></nb-icon>
      </span>
      <h6>{{ "pages.dozent.messages.selectLearnerTitle" | translate }}</h6>
      <p>{{ "pages.dozent.messages.selectLearnerFirst" | translate }}</p>
    </nb-card-body>

    <ng-container *ngIf="selectedLearner">
      <nb-card-header class="learner-head">
        <span class="avatar avatar--lg" [class.online]="selectedLearner.is_online">
          {{ getInitials(selectedLearner) }}
        </span>
        <span class="learner-head__text">
          <span class="learner-head__name">{{ getLearnerName(selectedLearner) }}</span>
          <span class="learner-head__mail">{{ selectedLearner.email }}</span>
        </span>
        <span class="presence-chip" [class.online]="selectedLearner.is_online">
          {{
            (selectedLearner.is_online
              ? "pages.dozent.messages.online"
              : "pages.dozent.messages.offline"
            ) | translate
          }}
        </span>
      </nb-card-header>

      <nb-tabset (changeTab)="onTabChange($event.tabId)">
        <!-- Tab: Conversations -->
        <nb-tab
          tabId="conversations"
          [tabTitle]="t.instant('pages.dozent.messages.tabConversations')"
        >
          <div class="panel-toolbar panel-toolbar--tab">
            <nb-select
              *ngIf="!isLoadingConversations && conversations.length > 0"
              size="small"
              [selected]="conversationSortMode"
              (selectedChange)="onConversationSortChange($event)"
              [placeholder]="'pages.dozent.messages.sortBy' | translate"
            >
              <nb-option value="recent">{{ "pages.dozent.messages.sortConversationRecent" | translate }}</nb-option>
              <nb-option value="oldest">{{ "pages.dozent.messages.sortConversationOldest" | translate }}</nb-option>
              <nb-option value="title_asc">{{ "pages.dozent.messages.sortTitleAsc" | translate }}</nb-option>
              <nb-option value="title_desc">{{ "pages.dozent.messages.sortTitleDesc" | translate }}</nb-option>
            </nb-select>

            <app-pagination
              *ngIf="!isLoadingConversations && conversations.length > 0"
              class="toolbar-pagination"
              [totalItems]="conversations.length"
              [itemsPerPage]="conversationPageSize"
              [currentPage]="conversationPage"
              [pageSizeOptions]="pageSizeOptions"
              (pageChanged)="onConversationPageChange($event)"
              (itemsPerPageChange)="onConversationPageSizeChange($event)"
            ></app-pagination>

            <button
              nbButton
              size="small"
              status="primary"
              class="toolbar-action"
              (click)="showNewConversationForm = !showNewConversationForm"
            >
              <nb-icon [icon]="showNewConversationForm ? 'close-outline' : 'plus-outline'"></nb-icon>
              {{ "pages.dozent.messages.newConversation" | translate }}
            </button>
          </div>

          <!-- New conversation composer (flat section — no card-on-card) -->
          <section class="composer" *ngIf="showNewConversationForm">
            <header class="composer__header">
              <nb-icon icon="edit-2-outline"></nb-icon>
              {{ "pages.dozent.messages.composerTitle" | translate }}
            </header>
            <input
              nbInput
              fullWidth
              fieldSize="small"
              [placeholder]="'pages.dozent.messages.titlePlaceholder' | translate"
              [(ngModel)]="newConversationTitle"
            />
            <textarea
              nbInput
              fullWidth
              [placeholder]="'pages.dozent.messages.messagePlaceholder' | translate"
              [(ngModel)]="newConversationMessage"
              rows="3"
            ></textarea>
            <div class="composer__actions">
              <button
                nbButton
                size="small"
                ghost
                (click)="showNewConversationForm = false"
              >
                {{ "buttons.cancel" | translate }}
              </button>
              <button
                nbButton
                size="small"
                status="primary"
                (click)="onCreateConversation()"
                [disabled]="!newConversationMessage.trim() || isCreating"
                [nbSpinner]="isCreating"
                nbSpinnerSize="tiny"
                nbSpinnerStatus="control"
              >
                {{ "buttons.create" | translate }}
              </button>
            </div>
          </section>

          <app-page-loader *ngIf="isLoadingConversations"></app-page-loader>

          <ul
            *ngIf="!isLoadingConversations && conversations.length > 0"
            class="conv-list"
          >
            <li
              *ngFor="let conv of pagedConversations"
              class="conv-item"
              [class.is-expanded]="selectedConversation?.id === conv.id"
              [class.is-escalation]="conv.source === 'ai_escalation' && conv.status === 'open'"
            >
              <button type="button" class="conv-header" alAnchor="dozent.conversation" (click)="onSelectConversation(conv)">
                <span class="conv-main">
                  <span class="conv-title-row">
                    <nb-icon
                      *ngIf="conv.source === 'ai_escalation'"
                      icon="flash-outline"
                      class="escalation-icon"
                      [nbTooltip]="'pages.dozent.messages.escalationTooltip' | translate"
                    ></nb-icon>
                    <span class="conv-title">
                      {{ conv.title || ("pages.dozent.messages.untitled" | translate) }}
                    </span>
                  </span>
                  <span class="conv-meta">
                    <span class="chip" [ngClass]="'chip--' + getStatusBadgeClass(conv.status)">
                      {{ getStatusLabel(conv.status) }}
                    </span>
                    <span *ngIf="conv.source === 'ai_escalation'" class="chip chip--escalation">
                      HITL
                    </span>
                    <span class="chip chip--quiet">{{ getSourceLabel(conv.source) }}</span>
                    <span *ngIf="conv.course_name" class="chip chip--course">
                      <nb-icon icon="book-outline"></nb-icon>{{ conv.course_name }}
                    </span>
                  </span>
                </span>
                <span class="conv-trailing">
                  <span class="conv-date">{{ conv.created_at | date: "dd.MM.yy HH:mm" }}</span>
                  <nb-icon
                    class="conv-chevron"
                    [icon]="selectedConversation?.id === conv.id ? 'chevron-up-outline' : 'chevron-down-outline'"
                  ></nb-icon>
                </span>
              </button>

              <div class="conv-body" *ngIf="selectedConversation?.id === conv.id">
                <div class="thread">
                  <div class="thread__bar">
                    <span class="thread__label">
                      <nb-icon icon="message-circle-outline"></nb-icon>
                      {{ getSourceLabel(conv.source) }}
                    </span>
                    <button
                      *ngIf="conv.status === 'open'"
                      nbButton
                      size="small"
                      status="basic"
                      class="btn-resolve"
                      alAnchor="dozent.resolve"
                      (click)="onResolveConversation()"
                    >
                      <nb-icon icon="checkmark-circle-2-outline"></nb-icon>
                      {{ "pages.dozent.messages.markResolved" | translate }}
                    </button>
                    <span *ngIf="conv.status === 'resolved'" class="chip chip--resolved">
                      <nb-icon icon="checkmark-circle-2-outline"></nb-icon>
                      {{ "pages.dozent.messages.statusResolved" | translate }}
                    </span>
                  </div>

                  <app-page-loader *ngIf="isLoadingMessages"></app-page-loader>

                  <div *ngIf="!isLoadingMessages" class="thread__scroll" #messageContainer>
                    <div
                      *ngFor="let msg of messages"
                      class="bubble"
                      [class.bubble--own]="isOwnMessage(msg)"
                      [class.bubble--other]="!isOwnMessage(msg)"
                    >
                      <span class="bubble__sender">{{ getSenderLabel(msg) }}</span>
                      <span class="bubble__text">{{ msg.message }}</span>
                      <span class="bubble__time">{{ msg.created_at | date: "HH:mm" }}</span>
                    </div>

                    <div *ngIf="messages.length === 0" class="thread__empty">
                      {{ "pages.dozent.messages.noMessages" | translate }}
                    </div>
                  </div>

                  <div class="thread__composer" *ngIf="conv.status === 'open'" alAnchor="dozent.composer">
                    <input
                      nbInput
                      fullWidth
                      [placeholder]="'pages.dozent.messages.typeMessage' | translate"
                      [(ngModel)]="newMessageText"
                      (keydown.enter)="onSendMessage()"
                      [disabled]="isSending"
                    />
                    <button
                      nbButton
                      status="primary"
                      class="send-button"
                      (click)="onSendMessage()"
                      [disabled]="!newMessageText.trim() || isSending"
                    >
                      <nb-icon icon="paper-plane-outline"></nb-icon>
                    </button>
                  </div>
                </div>
              </div>
            </li>
          </ul>

          <div
            *ngIf="!isLoadingConversations && conversations.length === 0"
            class="empty-block"
          >
            <span class="empty-block__icon"><nb-icon icon="inbox-outline"></nb-icon></span>
            <p>{{ "pages.dozent.messages.noConversations" | translate }}</p>
          </div>
        </nb-tab>

        <!-- Tab: Exercises -->
        <nb-tab
          tabId="exercises"
          [tabTitle]="t.instant('pages.dozent.messages.tabExercises')"
        >
          <app-page-loader *ngIf="isLoadingExercises"></app-page-loader>

          <ul *ngIf="!isLoadingExercises && exercises.length > 0" class="exercise-list">
            <li
              *ngFor="let ex of pagedExercises"
              class="exercise-item"
              [class.is-expanded]="expandedExercise === ex.id"
            >
              <button type="button" class="exercise-header" (click)="toggleExercise(ex.id)">
                <span class="exercise-main">
                  <span class="exercise-title">{{ ex.title }}</span>
                  <span class="exercise-meta">
                    <span class="chip chip--quiet">{{ ex.exercise_type }}</span>
                    <span *ngIf="ex.submitted_at" class="exercise-date">
                      <nb-icon icon="clock-outline"></nb-icon>
                      {{ ex.submitted_at | date: "dd.MM.yy" }}
                    </span>
                  </span>
                </span>
                <span class="exercise-trailing">
                  <span *ngIf="ex.ai_grade != null" class="chip chip--grade">AI {{ ex.ai_grade }}</span>
                  <span *ngIf="ex.dozent_grade != null" class="chip chip--grade chip--grade-dozent">
                    <nb-icon icon="award-outline"></nb-icon>{{ ex.dozent_grade }}
                  </span>
                  <nb-icon
                    class="conv-chevron"
                    [icon]="expandedExercise === ex.id ? 'chevron-up-outline' : 'chevron-down-outline'"
                  ></nb-icon>
                </span>
              </button>

              <div *ngIf="expandedExercise === ex.id" class="exercise-detail">
                <div class="detail-section" *ngIf="ex.task">
                  <label>{{ "pages.dozent.exercises.task" | translate }}</label>
                  <p>{{ ex.task }}</p>
                </div>
                <div class="detail-section" *ngIf="ex.answer">
                  <label>{{ "pages.dozent.messages.learnerAnswer" | translate }}</label>
                  <p>{{ ex.answer }}</p>
                </div>
                <div class="detail-section" *ngIf="ex.feedback">
                  <label>{{ "pages.dozent.messages.aiFeedback" | translate }}</label>
                  <p>{{ ex.feedback }}</p>
                </div>
              </div>
            </li>
          </ul>

          <div class="panel-toolbar panel-toolbar--footer" *ngIf="!isLoadingExercises && exercises.length > 0">
            <app-pagination
              class="toolbar-pagination"
              [totalItems]="exercises.length"
              [itemsPerPage]="exercisePageSize"
              [currentPage]="exercisePage"
              [pageSizeOptions]="pageSizeOptions"
              (pageChanged)="onExercisePageChange($event)"
              (itemsPerPageChange)="onExercisePageSizeChange($event)"
            ></app-pagination>
          </div>

          <div
            *ngIf="!isLoadingExercises && exercises.length === 0"
            class="empty-block"
          >
            <span class="empty-block__icon"><nb-icon icon="book-open-outline"></nb-icon></span>
            <p>{{ "pages.dozent.messages.noExercises" | translate }}</p>
          </div>
        </nb-tab>
      </nb-tabset>
    </ng-container>
  </nb-card>
</div>
